import type { AutocompleteProps, TextFieldProps } from '@mui/material';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useAsync, useImmutableFunction, usePreviousIfEquals } from '@niaratech/niara-react-components';
import shallowEqual from 'fbjs/lib/shallowEqual';
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import useMaskedInputText from '../../hooks/useMaskedInputText';
import { maskValue, unmaskValue } from '../../util/mask';
import type { InputOption, TextInputFieldProps } from '../types';
import { mergeRefs } from 'react-merge-refs';
export type InputTextProps = TextInputFieldProps & {
  /**
   * the user input will be converted to uppercase
   */
  uppercase?: boolean;
  /**
   * the user input will be converted to lowercase
   */
  lowercase?: boolean;
  /**
   * changes the size of the component
   */
  size?: 'medium' | 'small';
  /**
   * if a multiline textarea should be rendered
   */
  textarea?: boolean;
  /**
   * if the label should be hidden
   */
  hideLabel?: boolean;
  /**
   * type attribute for input (defaults to text)
   */
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  /**
   * addornment before the input
   */
  startAdornment?: React.ReactElement | string;
  endAdornment?: React.ReactElement | string;
  /**
   * if true, the input width will be 100%
   */
  fullWidth?: boolean;
  /**
   * Called when an option is selected (if options are provided)
   */
  onOptionSelected?: (option: InputOption<string>) => unknown;
  AutocompleteProps?: Partial<Omit<AutocompleteProps<string, false, false, false, any>, 'value' | 'inputValue' | 'onInputChange' | 'onChange' | 'onHighlightChange' | 'fullWidth' | 'options' | 'clearOnEscape' | 'selectOnFocus' | 'autoComplete' | 'clearOnBlur' | 'renderOption' | 'disabled' | 'getOptionLabel' | 'renderInput'>>;
  /**
   * Used only for admin theme
   */
  labelVariant?: 'notched';
  /**
   * If set, the value set directly into the html input will be "externally" controlled.
   */
  setInputValue?: React.Dispatch<React.SetStateAction<string>>;
  /**
   * If set, the value set directly into the html input will be "externally" controlled.
   */
  inputValue?: string;
  /**
   * Alternative to options. Called only on mount.
   */
  loadOptions?: () => Promise<Array<InputOption<string>>>;
  renderOption?: AutocompleteProps<InputOption<string>, false, false, false, any>['renderOption'];
  /**
   * if true, show maxLength in endAdornment.
   */
  showMaxLength?: boolean;
  /**
   * Não seleciona item ao fazer o highlight via keyboard
   */
  disableSetValueOnHighlight?: boolean;
  /**
   * Usado por InputAutoComplete - não atualiza o conteúdo do input text quando detecta mudança do value
   */
  _disableUpdateInputTextInputValueWhenValueChanged?: boolean;
  /**
   * Campo apenas para leitura
   */
  readOnly?: boolean;
  /**
   * permite conteúdo maior que a máscara (se informada)
   */
  allowLongerThanMask?: boolean | string;
  TextFieldProps?: Pick<TextFieldProps, 'inputProps' | 'InputProps' | 'variant'>;
  inputRef?: TextFieldProps['inputRef'];
  getOptionLabel?: (option: string) => string;
  /**
   * Limite das opções exibidas de autocomplete (quando é autocomplete)
   */
  listSize?: number;
  preserveOptionsOrder?: boolean;
};
const InputText = React.forwardRef(function InputText(props: InputTextProps, ref: React.Ref<HTMLInputElement>) {
  const {
    label,
    startAdornment,
    endAdornment,
    required,
    disabled,
    name,
    type,
    options: propOptions,
    getOptionLabel: propGetOptionLabel,
    autoComplete,
    mask,
    maskPattern,
    hint,
    value: propValue,
    setValue: propSetValue,
    errors,
    uppercase,
    lowercase,
    size,
    placeholder,
    textarea,
    hideLabel,
    id,
    fullWidth = true,
    onOptionSelected,
    AutocompleteProps,
    labelVariant,
    loadOptions,
    renderOption: propsRenderOption,
    maxLength,
    pattern,
    showMaxLength,
    disableSetValueOnHighlight,
    _disableUpdateInputTextInputValueWhenValueChanged,
    readOnly = false,
    allowLongerThanMask = false,
    TextFieldProps = {},
    inputRef,
    listSize,
    preserveOptionsOrder
  } = props;
  const immutableSetValue = useImmutableFunction(propSetValue);
  const isAutocomplete = propOptions != null || loadOptions != null;
  const [loadedOptionsPromise] = useState(() => loadOptions ? loadOptions() : null);
  const [loadedOptions,, loadingOptions] = useAsync(() => loadedOptionsPromise, [loadedOptionsPromise]);
  const _options = loadOptions != null ? loadedOptions : propOptions;
  const optionsToSorted = [...(_options || [])];
  const options = usePreviousIfEquals(preserveOptionsOrder ? optionsToSorted : optionsToSorted?.sort?.((a, b) => Number(a?.disabled || 0) - Number(b?.disabled || 0)), shallowEqual);
  const optionsString = useMemo(() => {
    if (options?.length > 0) {
      return options.map(r => r.value);
    }
    return [];
  }, [options]);
  const optionsShown = useMemo(() => {
    if (listSize >= 0 && optionsString != null && listSize < optionsString?.length) {
      return optionsString?.slice(0, listSize);
    }
    return optionsString;
  }, [listSize, optionsString]);
  const selectedOption = useMemo(() => options?.find(o => o.value === propValue), [options, propValue]);
  const useExternalInputValue = 'setInputValue' in props && props.setInputValue != null;
  const [_inputValue, _setInputValue] = useState<string>(() => selectedOption ? selectedOption.label : mask ? maskValue(propValue || '', mask, allowLongerThanMask) : propValue || '');
  const getOptionLabel = useCallback((v: string) => options?.find(r => r.value == v)?.label ?? '-', [options]);
  const renderOption = useImmutableFunction(propsRenderOption);

  // transforma o renderOption que veio via props para repassar ao <Autocomplete/>
  const newRenderOption = useCallback<AutocompleteProps<string, false, false, false, any>['renderOption']>((props, optionValue, state, ownerState) => {
    const option = options?.find(r => r.value == optionValue);
    return renderOption?.(props, option, state, ownerState as unknown as any);
  }, [options, renderOption]);
  const _setInputValue2 = useImmutableFunction('setInputValue' in props && useExternalInputValue && props.setInputValue ? props.setInputValue : _setInputValue);
  const inputValue = 'setInputValue' in props && useExternalInputValue ? props.inputValue : _inputValue;
  const {
    inputRef: maskInputRef,
    setInputValue
  } = useMaskedInputText({
    mask,
    allowLongerThanMask,
    inputValue,
    setInputValue: _setInputValue2
  });
  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(e => {
    const value = uppercase ? e.currentTarget.value?.toUpperCase() : lowercase ? e.currentTarget.value?.toLowerCase() : e.currentTarget.value;
    setInputValue(value);
    immutableSetValue(mask ? unmaskValue(maskValue(value, mask, allowLongerThanMask), mask, allowLongerThanMask) : value?.trim());
  }, [uppercase, lowercase, mask, immutableSetValue, allowLongerThanMask, setInputValue]);
  React.useEffect(() => {
    // tratamento de atualização de valor da props não disparada pelo
    // setValue deste componente
    if (isAutocomplete || _disableUpdateInputTextInputValueWhenValueChanged) {
      // do nothing
    } else if (propValue && mask) {
      setInputValue(inputValue => unmaskValue(maskValue(inputValue, mask, allowLongerThanMask), mask, allowLongerThanMask) != propValue ? maskValue(propValue, mask, allowLongerThanMask) : inputValue);
    } else {
      setInputValue(inputValue => inputValue?.trim() != propValue ? propValue || '' : inputValue);
    }
  }, [mask, propValue, isAutocomplete, setInputValue, _disableUpdateInputTextInputValueWhenValueChanged, allowLongerThanMask]);
  const onAutocompleteChange = useImmutableFunction<AutocompleteProps<any, any, any, any, any>['onChange']>((event, optionValue: string, reason) => {
    const selectedOption = options?.find(r => r.value === optionValue);
    onOptionSelected?.(selectedOption);
    immutableSetValue?.(optionValue);
  });
  useLayoutEffect(() => {
    if (_disableUpdateInputTextInputValueWhenValueChanged) {
      // quando é inputautocomplete, não atualizar o setInputValue quando mudar o value
      //do nothing
    } else if (selectedOption) {
      setInputValue(selectedOption.label);
    } else {
      setInputValue('');
    }
  }, [selectedOption, setInputValue, _disableUpdateInputTextInputValueWhenValueChanged]);
  const onInputChange = useImmutableFunction<AutocompleteProps<string, false, false, false, any>['onInputChange']>((e, newInputValue, reason) => {
    if (reason == 'input') {
      setInputValue(newInputValue);
    } else if (reason == 'clear') {
      setInputValue('');
    } else if (reason == 'reset' && e?.type == 'blur') {
      setInputValue(newInputValue);
    }
  });
  const onHighlightChange = useImmutableFunction<AutocompleteProps<string, false, false, false, any>['onHighlightChange']>((event, optionValue, reason) => {
    if (reason === 'keyboard' && !disableSetValueOnHighlight) {
      const selectedOption = options?.find(r => r.value === optionValue);
      onOptionSelected?.(selectedOption);
      immutableSetValue?.(optionValue);
    }
  });
  if (isAutocomplete) {
    return <Autocomplete<string> loading={loadingOptions} loadingText={<LinearProgress color="inherit" />} {...AutocompleteProps || {}} value={selectedOption?.value ?? ''} inputValue={inputValue || ''} onInputChange={onInputChange} onChange={onAutocompleteChange} onHighlightChange={onHighlightChange} fullWidth={fullWidth} options={optionsShown} getOptionDisabled={option => options?.find(op => op?.value === option)?.disabled} clearOnEscape selectOnFocus autoComplete clearOnBlur disablePortal renderOption={(props, option, state, ownerState) => {
      const currentOption = options?.find(op => op?.value === option);
      const optionLabel = renderOption ? currentOption?.label : currentOption?.disabled ? `${currentOption?.label} (${currentOption?.disabledReason ?? "Desabilitado"})` : currentOption?.label;
      return <li {...props} key={option}>
              {renderOption ? newRenderOption(props, option, state, ownerState) : optionLabel}
            </li>;
    }} disabled={disabled} getOptionLabel={propGetOptionLabel ?? getOptionLabel} renderInput={params => <TextField id={id} variant={TextFieldProps?.variant || 'outlined'} {...params} name={name} label={hideLabel ? undefined : label} placeholder={placeholder} required={required} autoComplete={autoComplete} fullWidth={fullWidth} type={type ?? 'text'} error={errors?.length > 0} disabled={disabled} inputProps={{
      ...params.inputProps,
      ...(TextFieldProps?.inputProps ?? {})
    }} InputLabelProps={{
      ...params.InputLabelProps,
      className: labelVariant == 'notched' ? 'NiaraVariantNotched' : undefined
    }} InputProps={{
      ...params.InputProps,
      ...(TextFieldProps?.InputProps ?? {}),
      className: labelVariant == 'notched' ? 'NiaraVariantNotched' : undefined,
      startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
      endAdornment: endAdornment ? <>
                  <InputAdornment position="end">{endAdornment}</InputAdornment>
                  {params.InputProps.endAdornment}
                </> : params.InputProps.endAdornment,
      notched: hideLabel ? false : labelVariant == 'notched' ? true : undefined
    }} FormHelperTextProps={{
      className: errors?.length > 0 ? 'NiaraErrorDescription' : undefined
    }} helperText={errors?.length > 0 ? errors.join(', ') : hint} size={size} inputRef={mergeRefs([inputRef, maskInputRef, ref].filter(Boolean))} />} />;
  }
  return <TextField id={id} value={inputValue ?? ''} placeholder={placeholder} onChange={onChange} name={name} label={hideLabel ? undefined : label} required={required} autoComplete={autoComplete} fullWidth={fullWidth} variant={'outlined'} type={type ?? 'text'} inputProps={{
    maxLength,
    pattern: mask ? maskPattern : pattern,
    ...(TextFieldProps?.inputProps ?? {})
  }} InputLabelProps={{
    className: labelVariant == 'notched' ? 'NiaraVariantNotched' : undefined
  }} InputProps={{
    ...(TextFieldProps?.InputProps ?? {}),
    className: labelVariant == 'notched' ? 'NiaraVariantNotched' : undefined,
    startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
    endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : maxLength && showMaxLength ? <InputAdornment position="end">{`${inputValue?.length ?? 0}/${maxLength}`}</InputAdornment> : undefined,
    notched: hideLabel ? false : labelVariant == 'notched' ? true : undefined,
    readOnly: readOnly,
    ref: ref
  }} FormHelperTextProps={{
    className: errors?.length > 0 ? 'NiaraErrorDescription' : undefined
  }} error={errors?.length > 0} disabled={disabled} helperText={errors?.length > 0 ? errors.join(', ') : hint} size={size} multiline={textarea} minRows={textarea ? 3 : undefined} inputRef={mergeRefs([inputRef, maskInputRef, ref].filter(Boolean))} />;
});
export default InputText;